/**
 * Sortierung für Tabellen: klickbare Spaltenköpfe.
 *
 * Anlass (Armin Kern, Bereichsleiter, 08.09.2026): „Alles, was eine Tabelle
 * ist, sollte filterbar / sortierbar sein." Denselben Punkt hatte er schon am
 * 17.08. geschickt.
 *
 * Bewusst EINE Mechanik für alle drei Tabellen (Mein Team, Teams, Mitarbeiter).
 * Drei eigene Umsetzungen würden sich unterschiedlich verhalten, und der
 * nächste Wunsch müsste dreimal gebaut werden.
 *
 * Bedienung:
 *   1. Klick  → sinnvollste Richtung zuerst: Zahlen absteigend (größter Umsatz
 *               oben), Text aufsteigend (A vor Z)
 *   2. Klick  → dreht um
 *   3. Klick  → zurück zur Standardsortierung der Tabelle
 *
 * Der dritte Klick ist wichtig: Die Standardsortierung stellt die auffälligen
 * Zeilen nach oben. Wer sie einmal verlassen hat, muss ohne Neuladen
 * zurückfinden können.
 *
 * Diese Datei enthält nur die Regeln und bleibt frei von React — nur so sind
 * sie ohne Oberfläche testbar. Der Hook für die Seiten steht in
 * `hooks/useTabellenSortierung.ts`. Getrennt seit 10.09.2026: Das Release Gate
 * auf GitHub führt die Unit-Tests aus, BEVOR es die UI-Pakete installiert; mit
 * React in dieser Datei scheiterte dort schon der Import des Tests.
 */

export type Richtung = "auf" | "ab";

/**
 * Eine sortierbare Spalte. `typ` legt nur die Richtung des ERSTEN Klicks fest
 * — bei Zahlen will man den größten Wert oben sehen, bei Namen A vor Z.
 */
export interface SortSpalte<T> {
  wert: (zeile: T) => number | string | null;
  typ: "zahl" | "text";
}

export type SortSpalten<T> = Record<string, SortSpalte<T>>;

export interface Sortierung {
  spalte: string;
  richtung: Richtung;
}

/** Richtung, in die der erste Klick auf diese Spalte sortiert. */
export function ersteRichtung<T>(spalte: SortSpalte<T> | undefined): Richtung {
  return spalte?.typ === "zahl" ? "ab" : "auf";
}

/**
 * Sortiert eine Kopie von `zeilen`. Ohne `sortierung` (oder bei unbekannter
 * Spalte) gilt die Standardreihenfolge der Tabelle.
 */
export function sortiereZeilen<T>(
  zeilen: readonly T[],
  spalten: SortSpalten<T>,
  sortierung: Sortierung | null,
  standard: (a: T, b: T) => number,
): T[] {
  const liste = [...zeilen];
  const spalte = sortierung ? spalten[sortierung.spalte] : undefined;
  if (!sortierung || !spalte) return liste.sort(standard);

  const lies = spalte.wert;
  const faktor = sortierung.richtung === "auf" ? 1 : -1;

  return liste.sort((a, b) => {
    const x = lies(a);
    const y = lies(b);
    // Fehlende Werte immer ans Ende — in BEIDEN Richtungen. Sonst stünden bei
    // "kleinste zuerst" lauter Striche oben und verdeckten das Ergebnis.
    if (x == null && y == null) return standard(a, b);
    if (x == null) return 1;
    if (y == null) return -1;
    if (typeof x === "number" && typeof y === "number") {
      return (x - y) * faktor || standard(a, b);
    }
    // Gleichstand fällt auf die Standardreihenfolge zurück, damit die Liste
    // bei einer Spalte mit wenigen Stufen (z. B. Ampel) nicht willkürlich wird.
    return String(x).localeCompare(String(y), "de") * faktor || standard(a, b);
  });
}
